/**
 * Performance Heatmap Component
 * Grid heatmap showing client performance across multiple metrics
 */

import React, { useState } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Card } from '@/components/ui/Card'
import { getHeatmapColor, truncateText } from './chart-utils'
import { ChartSkeleton } from './ChartSkeleton'
import { ChartError } from './ChartError'
import { useMediaQuery } from '@/hooks/useMediaQuery'

export interface HeatmapData {
  client: string
  metrics: Record<string, number> // metric name → variance %
}

export interface PerformanceHeatmapProps {
  data: HeatmapData[]
  selectedMetrics: string[]
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
  onCellClick?: (client: string, metric: string, value: number) => void
}

export const PerformanceHeatmap: React.FC<PerformanceHeatmapProps> = ({
  data,
  selectedMetrics,
  loading = false,
  error = null,
  onRetry,
  onCellClick
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null)

  if (loading) {
    return <ChartSkeleton width="100%" height={400} />
  }

  if (error) {
    return <ChartError message={error.message} onRetry={onRetry} width="100%" height={400} />
  }

  if (!data || data.length === 0) {
    return (
      <div className="w-full h-[400px] flex items-center justify-center bg-card rounded-lg border border-dashed border-border">
        <p className="text-sm text-muted-foreground">Dados não disponíveis</p>
      </div>
    )
  }

  // Mobile: render as stacked cards instead of grid
  if (isMobile) {
    return (
      <div className="w-full">
        {data.map((client, idx) => (
          <Card key={idx} className="mb-4 p-4 bg-card border border-border">
            <h3 className="text-sm font-semibold mb-2">{client.client}</h3>
            {selectedMetrics.map((metric) => {
              const variance = client.metrics[metric] || 0
              const color = getHeatmapColor(variance)

              return (
                <div
                  key={metric}
                  className="flex justify-between items-center py-1"
                >
                  <span className="text-xs text-muted-foreground">{metric}</span>
                  <span
                    className="text-xs text-white font-semibold px-2 py-0.5 rounded"
                    style={{ backgroundColor: color }}
                  >
                    {variance > 0 ? '+' : ''}
                    {variance.toFixed(1)}%
                  </span>
                </div>
              )
            })}
          </Card>
        ))}
      </div>
    )
  }

  // Desktop: render as grid with tooltips
  return (
    <TooltipProvider delayDuration={300}>
      <div
        role="table"
        aria-label="Performance heatmap showing client variance across metrics"
        className="w-full overflow-x-auto bg-card rounded-lg border border-border"
      >
        <div
          className="grid min-w-fit"
          style={{
            gridTemplateColumns: `180px repeat(${selectedMetrics.length}, minmax(120px, 1fr))`
          }}
        >
          {/* Header Row */}
          <div className="sticky left-0 z-[3] bg-background p-3 border-r border-b border-border">
            <span className="text-xs font-semibold">Cliente</span>
          </div>

          {selectedMetrics.map((metric, colIdx) => (
            <div
              key={metric}
              className="p-3 border-b border-border bg-background sticky top-0 z-[2] transition-opacity duration-200"
              style={{
                opacity: hoveredCell?.col === colIdx ? 1 : 0.7
              }}
            >
              <span className="text-xs font-semibold">{truncateText(metric, 15)}</span>
            </div>
          ))}

          {/* Data Rows */}
          {data.map((client, rowIdx) => (
            <React.Fragment key={rowIdx}>
              {/* Client name (sticky column) */}
              <div
                className="sticky left-0 z-[1] bg-card p-3 border-r border-b border-border transition-opacity duration-200"
                style={{
                  opacity: hoveredCell?.row === rowIdx ? 1 : 0.85
                }}
              >
                <span className="text-xs font-medium">{truncateText(client.client, 20)}</span>
              </div>

              {/* Metric cells */}
              {selectedMetrics.map((metric, colIdx) => {
                const variance = client.metrics[metric] || 0
                const color = getHeatmapColor(variance)
                const isHovered = hoveredCell?.row === rowIdx || hoveredCell?.col === colIdx

                return (
                  <Tooltip key={`${rowIdx}-${colIdx}`}>
                    <TooltipTrigger asChild>
                      <div
                        className="p-3 border-b border-border transition-all duration-200 hover:z-10"
                        style={{
                          backgroundColor: color,
                          cursor: onCellClick ? 'pointer' : 'default',
                          opacity: isHovered ? 1 : 0.9,
                          transform: isHovered ? 'scale(1.05)' : 'scale(1)'
                        }}
                        onMouseEnter={() => setHoveredCell({ row: rowIdx, col: colIdx })}
                        onMouseLeave={() => setHoveredCell(null)}
                        onClick={() => onCellClick?.(client.client, metric, variance)}
                      >
                        <span className="text-xs text-white font-semibold text-center block">
                          {variance > 0 ? '+' : ''}
                          {variance.toFixed(1)}%
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <div>
                        <p className="text-xs font-semibold">{client.client}</p>
                        <p className="text-xs mt-1">
                          {metric}: {variance > 0 ? '+' : ''}
                          {variance.toFixed(1)}%
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                )
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </TooltipProvider>
  )
}
